/**
 * Reconnect state lives in **localStorage**, keyed per class.
 *
 * It used to be sessionStorage, which is per-tab and gone the moment the tab
 * closes — and that turned out to be the real cause of "my link stopped
 * working": a student whose tab died (phone reclaiming memory, browser closed)
 * lost the credential, while the code in their URL had already been spent. Both
 * doors shut on someone whose lesson was still running.
 *
 * localStorage survives tab close and browser restart on the same device, which
 * is the common recovery case. The credential is no more powerful than before:
 * still bound to one user and one class, still dead at `end_at + grace`
 * server-side, still revoked by any generation bump.
 *
 * Entries carry an expiry and are pruned on every read, so a shared family
 * device does not accumulate usable credentials for classes long finished.
 */

const PREFIX = 'everhall-meet:reconnect:'

export interface ReconnectState {
  reconnectToken: string
  sessionId: number
  /** Epoch ms, mirroring the class window plus the server's grace. */
  expiresAt: number
  /** Latest rotated join code, so a reload works even if the token is rejected. */
  code?: string
  /**
   * What this participant last chose for their own devices. Remembered so a
   * reconnect restores their decision rather than switching a child's camera back
   * on for them.
   */
  audioEnabled?: boolean
  videoEnabled?: boolean
}

function keyFor(sessionId: number): string {
  return `${PREFIX}${sessionId}`
}

export function saveReconnect(state: ReconnectState): void {
  try {
    pruneExpired()
    localStorage.setItem(keyFor(state.sessionId), JSON.stringify(state))
  } catch {
    /* storage unavailable (private mode, quota) — recovery falls back to the URL */
  }
}

export function loadReconnect(sessionId?: number): ReconnectState | null {
  try {
    pruneExpired()
    const entries = liveEntries()

    // Without a session id (first boot from a bare link) accept the only live
    // entry if there is exactly one: guessing between two classes would be worse
    // than letting the server decide from the code.
    if (sessionId === undefined) return entries.length === 1 ? entries[0] : null

    return entries.find((entry) => entry.sessionId === sessionId) ?? null
  } catch {
    return null
  }
}

/**
 * Records the student's own device choices against their class, merging into
 * whatever reconnect state already exists rather than replacing it.
 */
export function rememberChoices(
  sessionId: number,
  choices: { audioEnabled: boolean; videoEnabled: boolean },
): void {
  try {
    const existing = loadReconnect(sessionId)
    if (!existing) return // nothing to attach to; a fresh connect will write it

    saveReconnect({
      ...existing,
      audioEnabled: choices.audioEnabled,
      videoEnabled: choices.videoEnabled,
    })
  } catch {
    /* storage unavailable — rejoin just falls back to camera off */
  }
}

export function clearReconnect(sessionId?: number): void {
  try {
    if (sessionId === undefined) {
      for (const key of storageKeys()) localStorage.removeItem(key)
      return
    }
    localStorage.removeItem(keyFor(sessionId))
  } catch {
    /* ignore */
  }
}

function storageKeys(): string[] {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i)
    if (key?.startsWith(PREFIX)) keys.push(key)
  }
  return keys
}

function liveEntries(): ReconnectState[] {
  const out: ReconnectState[] = []
  for (const key of storageKeys()) {
    const parsed = read(key)
    if (parsed) out.push(parsed)
  }
  return out
}

function pruneExpired(): void {
  const now = Date.now()
  for (const key of storageKeys()) {
    const parsed = read(key)
    if (!parsed || parsed.expiresAt <= now) localStorage.removeItem(key)
  }
}

function read(key: string): ReconnectState | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ReconnectState>
    if (
      typeof parsed.reconnectToken !== 'string' ||
      typeof parsed.sessionId !== 'number' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null
    }
    return parsed as ReconnectState
  } catch {
    return null
  }
}
